import {AsyncPipe} from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import {ELoginStatus} from '@app/auth-config/models/auth-state.model';
import {AuthConfigService} from '@app/auth-config/services/auth-config.service';
import {selectTokenStatus} from '@app/auth-config/state/auth.selector';
import {
  Email_Regex,
  MinChars_Regex,
  MinMaxChars_Regex,
  OneLower_OneUpper_OneDigit_OneSpecial_BetweenRangeChar_Regex,
  OnlyDigits_Regex,
  alphanumericWithSpecialCharactersAndDiacritics_Regex,
} from '@app/shared/constants';
import {IMedicalSpecialty} from '@app/shared/models/medical-specialty.model';
import {Store} from '@ngrx/store';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {Bind} from 'primeng/bind';
import {Button} from 'primeng/button';
import {Checkbox} from 'primeng/checkbox';
import {FloatLabel} from 'primeng/floatlabel';
import {InputText} from 'primeng/inputtext';
import {Message} from 'primeng/message';
import {Select} from 'primeng/select';
import {Observable, Subject, map, takeUntil, tap} from 'rxjs';
import {EActionStatus} from 'src/app/shared/enums/action-status.enum';
import {ICity} from 'src/app/shared/models/city.model';
import {ICountry} from 'src/app/shared/models/country.model';
import {IRegion} from 'src/app/shared/models/region.model';
import {ValidationService} from 'src/app/shared/services/validation.service';
import {CustomPasswordComponent} from '../../../shared/components/custom-password/custom-password.component';
import {LanguageSelectorComponent} from '../../../shared/components/language-selector/language-selector.component';
import {IPhysicianSignUpRequest} from '../../models/sign-up-request.model';
import {ISignUpForm} from '../../models/signup-form.model';
import {
  getRecaptchaToken,
  loadCities,
  loadCountries,
  loadMedicalSpecialties,
  loadRegions,
  resetCaptchaFromSignupRequestComponent,
  signUpRequest,
} from '../../state/sign-up.actions';
import {
  selectAccountRequestStatus,
  selectCaptchaResponse,
  selectCities,
  selectCountries,
  selectMedicalSpecialties,
  selectRegions,
} from '../../state/sign-up.selector';

@Component({
  selector: 'app-sign-up-request',
  templateUrl: './sign-up-request.component.html',
  styleUrls: ['./sign-up-request.component.scss'],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    Bind,
    FloatLabel,
    InputText,
    Message,
    CustomPasswordComponent,
    Select,
    Checkbox,
    Button,
    LanguageSelectorComponent,
    AsyncPipe,
    TranslatePipe,
  ],
})
export class SignUpRequestComponent implements OnInit, OnDestroy {
  private readonly formBuilder = inject(FormBuilder);
  private readonly validationService = inject(ValidationService);
  private readonly translateService = inject(TranslateService);
  private readonly store = inject(Store);
  private readonly authConfigService = inject(AuthConfigService);

  private readonly unsubscribe$ = new Subject<void>();
  private readonly countryCodeRequiredWhenPhoneValidator: ValidatorFn = (
    control: AbstractControl
  ): ValidationErrors | null => {
    const phoneNumberValue = (control.get('phoneNumber')?.value ?? '').trim();
    const countryCodeValue = (control.get('countryCode')?.value ?? '').trim();

    return phoneNumberValue && !countryCodeValue ? {countryCodeRequired: true} : null;
  };
  signupForm: FormGroup<ISignUpForm>;
  countries$: Observable<ICountry[]>;
  medicalSpecialties$: Observable<IMedicalSpecialty[]>;
  regions$: Observable<IRegion[]>;
  cities$: Observable<ICity[]>;
  accountRequestStatus$: Observable<EActionStatus>;
  actionStatus = EActionStatus;
  exceedTestLimit = '';
  allowCharecter = '';
  allowAlphanumeric = '';
  allowDigitChar = '';
  allowLetterDiacritics = '';
  allowAlphaNumericDiacritics = '';
  constructor() {
    this.signupForm = this.formBuilder.group(
      {
        firstName: [
          '',
          [
            Validators.required,
            this.validationService.charactersDiacriticsWithMaxLimt,
            Validators.pattern(MinChars_Regex),
          ],
        ],
        lastName: [
          '',
          [
            Validators.required,
            this.validationService.charactersDiacriticsWithMaxLimt,
            Validators.pattern(MinChars_Regex),
          ],
        ],
        email: ['', [Validators.required, Validators.pattern(Email_Regex)]],
        password: [
          '',
          [
            Validators.required,
            Validators.pattern(new RegExp(OneLower_OneUpper_OneDigit_OneSpecial_BetweenRangeChar_Regex)),
          ],
        ],
        countryCode: ['', []],
        phoneNumber: ['', [Validators.pattern(OnlyDigits_Regex)]],
        workAddress: [
          '',
          [
            Validators.required,
            this.validationService.alphanumericWithSpecialCharactersAndDiacritics,
            Validators.pattern(MinMaxChars_Regex),
          ],
        ],
        zipcode: ['', [Validators.required, this.validationService.alphanumericCharacters]],
        city: new FormControl<ICity | null>(null, Validators.required),
        country: new FormControl<ICountry | null>(null, Validators.required),
        county: new FormControl<IRegion | null>(null, Validators.required),
        medicalSpecialty: new FormControl<IMedicalSpecialty | null>(null, Validators.required),
        doctorId: ['', [this.validationService.specialCharacterWithDigits]],
        instituteName: [
          '',
          [Validators.required, Validators.pattern(alphanumericWithSpecialCharactersAndDiacritics_Regex)],
        ],
        isQualifiedProfessional: [false, [Validators.requiredTrue]],
      },
      {
        validators: [this.countryCodeRequiredWhenPhoneValidator],
      }
    );

    this.translateStaticText();
    this.countries$ = this.store.select(selectCountries).pipe(
      takeUntil(this.unsubscribe$),
      map(countries => {
        const flattenCountries = [...(countries ?? [])].sort((a, b) =>
          a.twoLetterIsoCode.localeCompare(b.twoLetterIsoCode)
        );
        return (
          flattenCountries.map(country => ({
            ...country,
            phoneNumberPrefixWithPlus: `(${country.twoLetterIsoCode}) +${country.phoneNumberPrefix}`,
          })) ?? []
        );
      })
    );
    this.regions$ = this.store.select(selectRegions).pipe(
      takeUntil(this.unsubscribe$),
      map(regions => {
        return regions ?? [];
      })
    );
    this.cities$ = this.store.select(selectCities).pipe(
      takeUntil(this.unsubscribe$),
      map(cities => {
        return cities ?? [];
      })
    );
    this.medicalSpecialties$ = this.store.select(selectMedicalSpecialties).pipe(
      takeUntil(this.unsubscribe$),
      map(medicalSpecialities => {
        return medicalSpecialities ?? [];
      })
    );
    this.accountRequestStatus$ = this.store.select(selectAccountRequestStatus);
  }

  private loadCities() {
    this.signupForm.get('city')?.setValue(null);
    const selectedCountry = this.signupForm.get('country')?.value;
    const selectedCounty = this.signupForm.get('county')?.value;
    if (selectedCountry && selectedCounty) {
      this.store.dispatch(
        loadCities({
          countryId: selectedCountry.id,
          regionId: selectedCounty.id,
        })
      );
    }
  }

  private translateStaticText() {
    this.exceedTestLimit = this.translateService.instant('SignUp.ExceedTestLimit');
    this.allowCharecter = this.translateService.instant('SignUp.AllowCharecter');
    this.allowAlphanumeric = this.translateService.instant('SignUp.AllowAlphanumeric');
    this.allowDigitChar = this.translateService.instant('SignUp.AllowDigitChar');
    this.allowLetterDiacritics = this.translateService.instant('SignUp.AllowLetterDiacritics');
    this.allowAlphaNumericDiacritics = this.translateService.instant('SignUp.AllowAlphaNumericDiacritics');
  }

  private createSignupRequest(): IPhysicianSignUpRequest {
    const selectedCity = this.signupForm.get('city')?.value ?? ({} as ICity);
    const selectedCountry = this.signupForm.get('country')?.value ?? ({} as ICountry);
    const selectedCounty = this.signupForm.get('county')?.value ?? ({} as IRegion);
    const medicalSpecialty = this.signupForm.get('medicalSpecialty')?.value ?? ({} as IMedicalSpecialty);
    const countryCode = this.signupForm.get('countryCode')?.value ?? '';
    const phoneNumber = this.signupForm.get('phoneNumber')?.value ?? '';
    return {
      firstName: this.signupForm.get('firstName')?.value ?? '',
      lastName: this.signupForm.get('lastName')?.value ?? '',
      email: this.signupForm.get('email')?.value ?? '',
      password: this.signupForm.get('password')?.value ?? '',
      address: this.signupForm.get('workAddress')?.value ?? '',
      zipCode: this.signupForm.get('zipcode')?.value ?? '',
      cityId: selectedCity.id,
      countryId: selectedCountry.id,
      regionId: selectedCounty.id,
      medicalSpecialtyId: medicalSpecialty.id,
      doctorId: this.signupForm.get('doctorId')?.value ?? '',
      institutionName: this.signupForm.get('instituteName')?.value ?? '',
      languageId: 0,
      organizationStatusId: 1,
      noWorkplaces: 0,
      institutionEmail: '',
      contractNo: '',
      phoneNumber: phoneNumber.trim(),
      prefixPhoneNumber: '+' + countryCode.trim(),
    } as IPhysicianSignUpRequest;
  }

  get control() {
    return this.signupForm.controls;
  }
  ngOnInit(): void {
    this.createAccount();
    this.store
      .select(selectTokenStatus)
      .pipe(
        takeUntil(this.unsubscribe$),
        tap(state => {
          if (state == ELoginStatus.Authorized || this.authConfigService.isTokenValid()) {
            this.store.dispatch(loadCountries());
            this.store.dispatch(loadMedicalSpecialties());
          }
        })
      )
      .subscribe();
  }
  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  onChangeCountry() {
    const selectedCountry = this.signupForm.get('country')?.value;
    if (selectedCountry) {
      this.signupForm.get('countryCode')?.setValue(selectedCountry.phoneNumberPrefix ?? '');
      this.store.dispatch(loadRegions({countryId: selectedCountry.id}));
    } else {
      this.signupForm.get('countryCode')?.setValue('');
    }
    this.signupForm.get('county')?.setValue(null);
    this.loadCities();
  }
  onChangeRegion() {
    this.loadCities();
  }

  onSubmit() {
    if (this.signupForm?.valid) {
      this.store.dispatch(resetCaptchaFromSignupRequestComponent());
      this.store.dispatch(getRecaptchaToken());
    }
  }

  private createAccount(): void {
    this.store
      .select(selectCaptchaResponse)
      .pipe(
        tap(captchaResponse => {
          if (captchaResponse) {
            this.store.dispatch(signUpRequest({request: this.createSignupRequest()}));
          }
        })
      )
      .subscribe();
  }
}
